import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import Layout from '../components/Layout';
import { 
  Calendar, Clock, Briefcase, FileText, TrendingUp,
  CheckCircle, XCircle, AlertCircle, DollarSign, Award,
  Users, Bell, ArrowRight, MapPin, LogIn, LogOut,
  CalendarDays, ClipboardList, Target, Wallet, Coffee
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

function EmployeeDashboard({ user, onLogout }) {
  const [stats, setStats] = useState({
    attendance: { present: 0, absent: 0, late: 0, percentage: 87 },
    leaves: { available: 15, taken: 0, pending: 0 },
    tasks: { total: 0, completed: 0, pending: 0 }
  });
  const [todayAttendance, setTodayAttendance] = useState(null);
  const [loading, setLoading] = useState(true);
  const [upcomingHolidays, setUpcomingHolidays] = useState([]);
  const [recentActivities, setRecentActivities] = useState([]);
  const [checkingIn, setCheckingIn] = useState(false);

  useEffect(() => {
    fetchEmployeeData();
  }, []);

  const fetchEmployeeData = async () => {
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      const [attendanceRes, leaveBalanceRes, todayRes, holidaysRes] = await Promise.all([
        axios.get(`${API}/api/attendance/summary`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/api/leaves/balance`, { headers }).catch(() => ({ data: { balances: [] } })),
        axios.get(`${API}/api/attendance/today`, { headers }).catch(() => ({ data: {} })),
        axios.get(`${API}/api/holidays/upcoming?limit=5`, { headers }).catch(() => ({ data: [] }))
      ]);

      // Process attendance
      const attData = attendanceRes.data;
      setStats(prev => ({
        ...prev,
        attendance: {
          present: attData.present_days || 20,
          absent: attData.absent_days || 2,
          late: attData.late_count || 1,
          percentage: attData.present_days ? Math.round((attData.present_days / (attData.present_days + (attData.absent_days || 0))) * 100) : 87
        }
      }));

      // Process leave balance
      const balances = leaveBalanceRes.data?.balances || [];
      const totalAvailable = balances.reduce((sum, b) => sum + (b.available || 0), 0);
      const totalPending = balances.reduce((sum, b) => sum + (b.pending || 0), 0);
      setStats(prev => ({
        ...prev,
        leaves: {
          available: totalAvailable || 15,
          taken: balances.reduce((sum, b) => sum + (b.used || 0), 0),
          pending: totalPending
        }
      }));

      // Today's attendance
      setTodayAttendance(todayRes.data);

      // Holidays
      setUpcomingHolidays(Array.isArray(holidaysRes.data) ? holidaysRes.data : []);

      // Mock recent activities
      setRecentActivities([
        { type: 'attendance', icon: LogIn, message: 'Checked in at 9:15 AM', time: 'Today', color: 'text-green-600' },
        { type: 'leave', icon: CheckCircle, message: 'Leave request approved', time: 'Yesterday', color: 'text-blue-600' },
        { type: 'task', icon: Target, message: 'Task assigned: Project Review', time: '2 days ago', color: 'text-purple-600' }
      ]);

    } catch (error) {
      console.error('Failed to fetch employee data:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleCheckIn = async () => {
    setCheckingIn(true);
    try {
      const token = localStorage.getItem('token');
      
      // Get location
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      await axios.post(`${API}/api/attendance/checkin`, null, {
        params: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          address: 'Office Location'
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchEmployeeData();
    } catch (error) {
      console.error('Check-in failed:', error);
      alert('Check-in failed. Please try again.');
    } finally {
      setCheckingIn(false);
    }
  };

  const handleCheckOut = async () => {
    setCheckingIn(true);
    try {
      const token = localStorage.getItem('token');
      
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject);
      });

      await axios.post(`${API}/api/attendance/checkout`, null, {
        params: {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          address: 'Office Location'
        },
        headers: { Authorization: `Bearer ${token}` }
      });

      fetchEmployeeData();
    } catch (error) {
      console.error('Check-out failed:', error);
      alert('Check-out failed. Please try again.');
    } finally {
      setCheckingIn(false);
    }
  };

  const StatCard = ({ title, value, subtitle, icon: Icon, bgColor, iconBg, link }) => (
    <Link to={link || '#'} className="bg-white rounded-xl shadow-sm hover:shadow-lg transition-all duration-300 p-5 border border-gray-100">
      <div className="flex items-center gap-4">
        <div className={`p-3 rounded-xl ${iconBg}`}>
          <Icon className="w-6 h-6" style={{ color: bgColor }} />
        </div>
        <div>
          <p className="text-sm text-gray-500">{title}</p>
          <h3 className="text-2xl font-bold" style={{ color: '#1a1f3a' }}>{value}</h3>
          <p className="text-xs text-gray-400">{subtitle}</p>
        </div>
      </div>
    </Link>
  );

  if (loading) {
    return (
      <Layout user={user} onLogout={onLogout}>
        <div className="flex items-center justify-center h-screen">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2" style={{ borderColor: '#2b3380' }}></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="p-6 bg-gray-50 min-h-screen">
        {/* Welcome Header */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold" style={{ color: '#1a1f3a' }}>
            Welcome, {user?.first_name || user?.name || 'Employee'}!
          </h1>
          <p className="text-gray-500">Here's your daily overview</p>
        </div>

        {/* Attendance Card - Prominent */}
        <div className="bg-gradient-to-r from-indigo-600 to-purple-600 rounded-2xl p-6 mb-6 text-white shadow-lg">
          <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
            <div>
              <h2 className="text-lg font-medium opacity-90">Today's Attendance</h2>
              <p className="text-3xl font-bold mt-1">
                {new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' })}
              </p>
              {todayAttendance?.checked_in && (
                <p className="text-sm mt-2 opacity-80">
                  <Clock className="w-4 h-4 inline mr-1" />
                  Checked in at {new Date(todayAttendance.attendance?.check_in_time).toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' })}
                </p>
              )}
            </div>
            <div className="flex gap-3">
              {!todayAttendance?.checked_in ? (
                <button
                  onClick={handleCheckIn}
                  disabled={checkingIn}
                  className="px-6 py-3 bg-white text-indigo-600 rounded-xl font-semibold hover:bg-opacity-90 transition-all flex items-center gap-2 shadow-md"
                >
                  <LogIn className="w-5 h-5" />
                  {checkingIn ? 'Please wait...' : 'Check In'}
                </button>
              ) : !todayAttendance?.checked_out ? (
                <button
                  onClick={handleCheckOut}
                  disabled={checkingIn}
                  className="px-6 py-3 bg-white text-purple-600 rounded-xl font-semibold hover:bg-opacity-90 transition-all flex items-center gap-2 shadow-md"
                >
                  <LogOut className="w-5 h-5" />
                  {checkingIn ? 'Please wait...' : 'Check Out'}
                </button>
              ) : (
                <div className="px-6 py-3 bg-white bg-opacity-20 rounded-xl flex items-center gap-2">
                  <CheckCircle className="w-5 h-5" />
                  <span>Day Complete</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Stats Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Attendance Rate"
            value={`${stats.attendance.percentage}%`}
            subtitle={`${stats.attendance.present} days present`}
            icon={Calendar}
            bgColor="#4caf50"
            iconBg="bg-green-50"
            link="/my-attendance"
          />
          <StatCard
            title="Leave Balance"
            value={stats.leaves.available}
            subtitle={`${stats.leaves.pending} pending`}
            icon={CalendarDays}
            bgColor="#ff9800"
            iconBg="bg-orange-50"
            link="/my-leaves"
          />
          <StatCard
            title="Pending Tasks"
            value={stats.tasks.pending || 4}
            subtitle={`${stats.tasks.completed || 8} completed`}
            icon={ClipboardList}
            bgColor="#7e57c2"
            iconBg="bg-purple-50"
            link="/my-tasks"
          />
          <StatCard
            title="Performance"
            value="4.2"
            subtitle="Out of 5.0"
            icon={Award}
            bgColor="#2b3380"
            iconBg="bg-indigo-50"
            link="/my-performance"
          />
        </div>

        {/* Quick Actions & Info */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Quick Actions */}
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <h2 className="font-semibold text-gray-800 mb-4">Quick Actions</h2>
            <div className="space-y-3">
              <Link to="/apply-leave" className="flex items-center gap-3 p-4 bg-orange-50 rounded-xl hover:bg-orange-100 transition-colors">
                <CalendarDays className="w-5 h-5 text-orange-600" />
                <span className="text-sm font-medium text-gray-700">Apply for Leave</span>
                <ArrowRight className="w-4 h-4 text-gray-400 ml-auto" />
              </Link>
              <Link to="/travel-expense" className="flex items-center gap-3 p-4 bg-green-50 rounded-xl hover:bg-green-100 transition-colors">
                <Wallet className="w-5 h-5 text-green-600" />
                <span className="text-sm font-medium text-gray-700">Submit Expense</span>
                <ArrowRight className="w-4 h-4 text-gray-400 ml-auto" />
              </Link>
              <Link to="/attendance-regularization" className="flex items-center gap-3 p-4 bg-blue-50 rounded-xl hover:bg-blue-100 transition-colors">
                <Clock className="w-5 h-5 text-blue-600" />
                <span className="text-sm font-medium text-gray-700">Regularize Attendance</span>
                <ArrowRight className="w-4 h-4 text-gray-400 ml-auto" />
              </Link>
              <Link to="/my-profile" className="flex items-center gap-3 p-4 bg-purple-50 rounded-xl hover:bg-purple-100 transition-colors">
                <Users className="w-5 h-5 text-purple-600" />
                <span className="text-sm font-medium text-gray-700">My Profile</span>
                <ArrowRight className="w-4 h-4 text-gray-400 ml-auto" />
              </Link>
            </div>
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <h2 className="font-semibold text-gray-800 mb-4">Recent Activity</h2>
            <div className="space-y-3">
              {recentActivities.map((activity, idx) => (
                <div key={idx} className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                  <activity.icon className={`w-5 h-5 mt-0.5 ${activity.color}`} />
                  <div className="flex-1">
                    <p className="text-sm text-gray-700">{activity.message}</p>
                    <p className="text-xs text-gray-400">{activity.time}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Upcoming Holidays */}
          <div className="bg-white rounded-xl shadow-sm p-5 border border-gray-100">
            <div className="flex items-center justify-between mb-4">
              <h2 className="font-semibold text-gray-800">Upcoming Holidays</h2>
              <Link to="/holiday-calendar" className="text-sm text-indigo-600 hover:underline">View All</Link>
            </div>
            {upcomingHolidays.length > 0 ? (
              <div className="space-y-3">
                {upcomingHolidays.map((holiday, idx) => (
                  <div key={idx} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg">
                    <div className="w-12 h-12 flex flex-col items-center justify-center bg-indigo-100 rounded-lg">
                      <span className="text-xs font-medium text-indigo-600">
                        {new Date(holiday.date).toLocaleDateString('en', { month: 'short' })}
                      </span>
                      <span className="text-lg font-bold text-indigo-700">
                        {new Date(holiday.date).getDate()}
                      </span>
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-800">{holiday.name}</p>
                      <p className="text-xs text-gray-500">{holiday.type || 'Holiday'}</p>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center py-8 text-gray-400">
                <Coffee className="w-12 h-12 mb-2" />
                <p className="text-sm">No upcoming holidays</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default EmployeeDashboard;
